import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BellRing,
  Cable,
  Cpu,
  Database,
  HardDrive,
  Lock,
  Network,
  Server,
  ShieldCheck,
  TerminalSquare,
} from 'lucide-react';
import SEO from '@/components/common/SEO';
import Header from '@/components/layout/Header';
import CorporateFooter from '@/components/layout/CorporateFooter';
import MainLayout from '@/components/layout/MainLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const glanceItems = [
  { label: 'Target board', value: 'Radxa ROCK 4C+ (RK3399, 4GB)' },
  { label: 'Operating system', value: 'Armbian Bookworm (Debian 12)' },
  { label: 'Service stack', value: 'FastAPI, SQLite, LanceDB, sentence-transformers (CPU)' },
  { label: 'HTTP endpoint', value: ':8765' },
  { label: 'UDP discovery', value: ':8766' },
  { label: 'mDNS', value: 'maplenode.local · _maplenode._tcp' },
] as const;

const capabilities = [
  {
    icon: Database,
    title: 'Local-first semantic memory',
    description:
      'Vector-searchable semantic memory lives on-device, reducing cloud dependency on the hot path and keeping cognition physically close to the user environment.',
  },
  {
    icon: HardDrive,
    title: 'Document upload and indexing',
    description:
      'Documents can be uploaded, chunked, indexed, and searched as part of the edge memory surface that MapleOS can consume.',
  },
  {
    icon: TerminalSquare,
    title: 'MCP-compatible tool server',
    description:
      'MapleNode exposes MCP-style tooling for discovery, device status, and safe diagnostic commands without exposing an unrestricted shell.',
  },
  {
    icon: BellRing,
    title: 'Zero-manual discovery',
    description:
      'mDNS publishes maplenode.local and UDP capability broadcasts announce the node every 30 seconds so MapleOS can auto-detect it on the LAN.',
  },
  {
    icon: ShieldCheck,
    title: 'Write-gated security model',
    description:
      'Write endpoints require the setup token via X-MapleSeed-Token, while open discovery endpoints remain safe for initial probing and pairing.',
  },
  {
    icon: Cpu,
    title: 'Edge cognition, not GPU hosting',
    description:
      'MapleNode is positioned as an edge memory and orchestration appliance, not a general-purpose GPU server or public cloud replacement.',
  },
] as const;

const pairingSteps = [
  'MapleOS discovers the node through mDNS or the UDP capability broadcast on the local network.',
  'The operator reads the setup token from the appliance console or SSH session.',
  'The token is pasted into MapleOS to establish trust and unlock write operations.',
  'MapleOS uses the token for memory writes, document indexing, and secured MCP-style calls.',
] as const;

const securityItems = [
  'LAN-only by design. The service is intended for local networks, not public internet exposure.',
  'Write-gated endpoints. Non-open write operations require the setup token.',
  'No arbitrary shell access. Diagnostic commands are intentionally allowlisted.',
  'Systemd hardening. The service runs with restricted privileges and controlled write paths.',
] as const;

const apiPreview = `TOKEN=<setup token>
BASE=http://maplenode.local:8765

curl -s $BASE/health
curl -s $BASE/capabilities
curl -s $BASE/mcp/tools

curl -s -X POST $BASE/memory/store \\
  -H "X-MapleSeed-Token: $TOKEN" \\
  -H 'Content-Type: application/json' \\
  -d '{"text":"User prefers local-first agents."}'`;

const discoveryPreview = `{
  "type": "maplenode.advertise",
  "name": "maplenode",
  "hostname": "maplenode.local",
  "api": "http://maplenode.local:8765",
  "version": "0.1.0",
  "capabilities": ["memory", "documents", "mcp"]
}`;

export default function MapleNode() {
  return (
    <MainLayout>
      <SEO
        title="MapleNode"
        description="MapleNode is CanXP AI's sovereign edge cognition appliance for MapleOS, combining local-first semantic memory, document indexing, and MCP-compatible edge tooling."
        url="/maplenode"
      />

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <section className="relative overflow-hidden px-4 pb-20 pt-20 md:pb-28 md:pt-28">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,rgba(220,38,38,0.17),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(220,38,38,0.10),transparent_28%)]" />
          <div className="container mx-auto max-w-6xl">
            <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
              <div>
                <Badge variant="outline" className="border-primary/25 bg-background/70 text-primary">
                  Product · MapleNode
                </Badge>
                <h1 className="mt-6 text-4xl font-semibold tracking-tight md:text-6xl">
                  Sovereign edge cognition appliance for MapleOS.
                </h1>
                <p className="mt-6 max-w-3xl text-lg leading-8 text-muted-foreground">
                  MapleNode brings local-first semantic memory, document indexing, discovery, and MCP-compatible tools
                  to the edge. It is designed as an always-on node that MapleOS can find, pair with, and use as a
                  trusted cognition surface on the LAN.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Badge className="bg-primary/10 text-primary">Local-first memory</Badge>
                  <Badge className="bg-primary/10 text-primary">MCP-compatible</Badge>
                  <Badge className="bg-primary/10 text-primary">LAN-native discovery</Badge>
                </div>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="lg" className="gap-2">
                    <a href="https://github.com/CanXPAI/maplenode" target="_blank" rel="noopener noreferrer">
                      View MapleNode Repository
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link to="/enterprise">Discuss Deployment</Link>
                  </Button>
                </div>
              </div>

              <Card className="border-border/70 bg-card/80 shadow-[0_24px_90px_-50px_rgba(220,38,38,0.7)]">
                <CardHeader>
                  <CardTitle className="text-2xl tracking-tight">At a glance</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                  {glanceItems.map((item) => (
                    <div key={item.label} className="rounded-xl border border-border/70 bg-background/70 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary/80">{item.label}</p>
                      <p className="mt-2 text-sm leading-6 text-foreground/90">{item.value}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="px-4 py-8">
          <div className="container mx-auto max-w-6xl">
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {capabilities.map((capability) => {
                const Icon = capability.icon;
                return (
                  <Card key={capability.title} className="h-full border-border/70 bg-background/80">
                    <CardHeader>
                      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <CardTitle className="text-2xl tracking-tight">{capability.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm leading-7 text-muted-foreground">{capability.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="border-y border-border/60 bg-card/30 px-4 py-20">
          <div className="container mx-auto max-w-6xl">
            <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
              <div>
                <Badge variant="outline" className="border-primary/25 bg-background/70 text-primary">
                  Discovery and pairing
                </Badge>
                <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-5xl">
                  MapleNode is designed to be found and trusted quickly.
                </h2>
                <p className="mt-5 text-lg leading-8 text-muted-foreground">
                  The pairing model is intentionally simple: discover the device, retrieve the setup token, and then let
                  MapleOS use that token for secured interactions. This keeps onboarding lightweight while preserving
                  control over writes and tool execution.
                </p>
                <div className="mt-8 space-y-4">
                  {pairingSteps.map((step, index) => (
                    <div key={step} className="flex items-start gap-4">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                        {index + 1}
                      </div>
                      <p className="text-sm leading-7 text-muted-foreground">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-6">
                <Card className="border-border/70 bg-background/80">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-2xl tracking-tight">
                      <Cable className="h-5 w-5 text-primary" />
                      Example capability broadcast
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="overflow-x-auto rounded-xl border border-border/70 bg-muted/30 p-4 text-xs leading-6 text-foreground/90">
                      <code>{discoveryPreview}</code>
                    </pre>
                  </CardContent>
                </Card>

                <Card className="border-border/70 bg-background/80">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-2xl tracking-tight">
                      <Server className="h-5 w-5 text-primary" />
                      Example API surface
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="overflow-x-auto rounded-xl border border-border/70 bg-muted/30 p-4 text-xs leading-6 text-foreground/90">
                      <code>{apiPreview}</code>
                    </pre>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-20">
          <div className="container mx-auto max-w-6xl">
            <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
              <div>
                <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                  Security posture
                </Badge>
                <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-5xl">
                  Edge-native, restrictive by default, and designed to stay local.
                </h2>
                <p className="mt-5 text-lg leading-8 text-muted-foreground">
                  MapleNode’s public positioning emphasizes a conservative security model for local deployment: limited
                  exposure, explicit write authorization, safe diagnostics, and restricted runtime privileges.
                </p>
              </div>

              <Card className="border-border/70 bg-background/80">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {securityItems.map((item) => (
                      <div key={item} className="flex items-start gap-3 rounded-xl border border-border/70 bg-muted/20 p-4">
                        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <p className="text-sm leading-7 text-muted-foreground">{item}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="px-4 pb-20">
          <div className="container mx-auto max-w-5xl rounded-[2rem] border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-background p-8 text-center md:p-10">
            <Badge variant="outline" className="border-primary/25 bg-background/70 text-primary">
              MapleOS companion product
            </Badge>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-5xl">
              MapleNode extends MapleOS into sovereign edge environments.
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-muted-foreground">
              It gives MapleOS a discoverable local cognition appliance for memory, document workflows, and secure edge
              tool execution without depending on a remote cloud service for every operation.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Button asChild size="lg" className="gap-2">
                <a href="https://github.com/CanXPAI/maplenode" target="_blank" rel="noopener noreferrer">
                  Open MapleNode Repo
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/mapleos">View MapleOS</Link>
              </Button>
            </div>
          </div>
        </section>

        <CorporateFooter />
      </div>
    </MainLayout>
  );
}
